// MikroTik hotspot CHAP: parol maydoniga `md5(chap-id + parol + chap-challenge)` yuboriladi —
// router shablonidagi `hexMD5('$(chap-id)' + password + '$(chap-challenge)')` bilan bir xil.

export interface ChapChallenge {
  id: string
  challenge: string
}

const HEX_BYTES = /^(?:[0-9a-f]{2})+$/

export function normalizeHexBytes(raw: string | null | undefined): string | null {
  const hex = raw?.trim().toLowerCase() ?? ''
  return HEX_BYTES.test(hex) ? hex : null
}

export function chapPassword(chap: ChapChallenge, password: string): string {
  const id = hexToBytes(chap.id)
  const secret = new TextEncoder().encode(password)
  const challenge = hexToBytes(chap.challenge)
  const message = new Uint8Array(id.length + secret.length + challenge.length)
  message.set(id, 0)
  message.set(secret, id.length)
  message.set(challenge, id.length + secret.length)
  return md5Hex(message)
}

function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2)
  for (let i = 0; i < bytes.length; i++) bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16)
  return bytes
}

const SHIFTS = [7, 12, 17, 22, 5, 9, 14, 20, 4, 11, 16, 23, 6, 10, 15, 21]
const SINES = Array.from({ length: 64 }, (_, i) => Math.floor(Math.abs(Math.sin(i + 1)) * 2 ** 32) >>> 0)

export function md5Hex(message: Uint8Array): string {
  const paddedLength = (((message.length + 8) >>> 6) + 1) << 6
  const buffer = new Uint8Array(paddedLength)
  buffer.set(message)
  buffer[message.length] = 0x80
  const view = new DataView(buffer.buffer)
  const bitLength = message.length * 8
  view.setUint32(paddedLength - 8, bitLength >>> 0, true)
  view.setUint32(paddedLength - 4, Math.floor(bitLength / 2 ** 32), true)

  let a0 = 0x67452301
  let b0 = 0xefcdab89
  let c0 = 0x98badcfe
  let d0 = 0x10325476

  for (let offset = 0; offset < paddedLength; offset += 64) {
    let a = a0
    let b = b0
    let c = c0
    let d = d0
    for (let i = 0; i < 64; i++) {
      const round = i >>> 4
      let f: number
      let g: number
      if (round === 0) {
        f = (b & c) | (~b & d)
        g = i
      } else if (round === 1) {
        f = (d & b) | (~d & c)
        g = (5 * i + 1) % 16
      } else if (round === 2) {
        f = b ^ c ^ d
        g = (3 * i + 5) % 16
      } else {
        f = c ^ (b | ~d)
        g = (7 * i) % 16
      }
      const sum = (a + f + (SINES[i] ?? 0) + view.getUint32(offset + g * 4, true)) | 0
      const shift = SHIFTS[round * 4 + (i % 4)] ?? 0
      a = d
      d = c
      c = b
      b = (b + ((sum << shift) | (sum >>> (32 - shift)))) | 0
    }
    a0 = (a0 + a) | 0
    b0 = (b0 + b) | 0
    c0 = (c0 + c) | 0
    d0 = (d0 + d) | 0
  }

  const digest = new DataView(new ArrayBuffer(16))
  ;[a0, b0, c0, d0].forEach((word, i) => digest.setUint32(i * 4, word, true))
  return Array.from(new Uint8Array(digest.buffer), (byte) => byte.toString(16).padStart(2, '0')).join('')
}
